/**
 * Role-based access control (AUTH-004). Roles grant default permissions;
 * agencies may additionally grant individual permissions per user.
 */

export const ROLES = ['super_admin', 'agency_admin', 'supervisor', 'clerk', 'process_server', 'customer', 'auditor', 'dispatcher', 'magistrate', 'solicitor'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super Admin',
  agency_admin: 'Agency Admin',
  supervisor: 'Supervisor',
  clerk: 'Clerk / Reception',
  process_server: 'Process Server',
  customer: 'Customer',
  auditor: 'Auditor (read-only)',
  dispatcher: 'Dispatcher',
  magistrate: 'Magistrate / Judge',
  solicitor: 'Solicitor / Prosecutor',
};

export const PERMISSIONS = [
  'papers.read',
  'papers.read_assigned',
  'papers.intake',
  'papers.review',
  'papers.hold',
  'papers.mark_urgent',
  'papers.switch_rule_version',
  'papers.close',
  'documents.upload',
  'documents.view',
  'payments.record',
  'payments.override',
  'custody.scan',
  'custody.authorize_transfer',
  'custody.mark_lost',
  'custody.court_return',
  'attempts.record',
  'attempts.calendar_override',
  'dispositions.finalize_non_service',
  'dispositions.override',
  'dispositions.review',
  'affidavits.sign',
  'affidavits.amend',
  'setout.schedule',
  'sync.review_conflicts',
  'audit.read',
  'admin.users',
  'admin.config',
  'admin.rules',
  'dispatch.view',
  'safety.respond',
  'schedule.manage',
  'bolo.manage',
  'affidavits.notarize',
  /** Add courts and customers (law firms, magistrates…) from intake screens. */
  'directory.add',
  /** RMS: write incident reports; read everyone's; approve (supervisor review). */
  'reports.write',
  'reports.read',
  'reports.approve',
  /** Evidence custodian: check items in, move, check out / in, release, destroy. */
  'evidence.manage',
  /** Court: see the court calendar; schedule trials, hearings and court security (magistrate, chosen supervisors). */
  'court.view',
  'court.schedule',
  /** Agency directory: phone, call number, shift, supervisor, car. */
  'directory.view',
  /** Set up the org chart: divisions, shifts, units, who supervises them, who's in them. */
  'admin.org',
  /** Quartermaster: add and change fleet vehicles and equipment, issue and take back. */
  'fleet.manage',
  /** Nonferrous metal (scrap) permits: take applications, issue, deny, suspend, revoke. */
  'permits.manage',
  /** Case files for the solicitor: share a case and upload its files (staff); see shared cases (solicitor). */
  'casefiles.share',
  'casefiles.view',
  /** Investigations: adopt cases, assign investigators, set case status. */
  'cases.investigate',
  /** Warrants: search active warrants; request one; enter / manage warrants and process requests; sign (judge). */
  'warrants.view',
  'warrants.request',
  'warrants.enter',
  'warrants.sign',
  /** Training: see everyone's training and certification status (supervisors); run the training program (training officer). */
  'training.view',
  'training.manage',
  /** Anonymous tips: read and work the tip queue. */
  'tips.manage',
  /** PIO: write media releases and case statements; approve them (command staff). */
  'pio.write',
  'pio.approve',
  /** Personnel: see and manage every write-up, commendation and complaint (HR / command). Supervisors write their own. */
  'personnel.manage',
  /** Early warning (Act 218): see officers' indicators, review alerts, close use of force reviews (command staff / IA). */
  'ews.review',
  /** HR: employee records, pay plan and everyone's pay, all evaluations. */
  'hr.manage',
  /** Schools: the school resource officers' schools and lockdown drills. */
  'schools.manage',
  /** Policies: write and publish policies, choose who must sign, import signature history, see who hasn't read them. */
  'policies.manage',
  /** Monthly dues programs (e.g. No-Shave): who's in, record payments (the secretary). */
  'dues.manage',
  /** Internal affairs: open and run IA cases and see every one (the IA investigator / command staff). */
  'ia.manage',
  /** Start a command board (bush bond, perimeter, search) and alert who's on duty / on standby. Agencies can let every deputy do it. */
  'command.start',
  /** See resources on duty: who on shift is BA, SFST, drone, EMT, entry shield… Agencies can let every deputy see it. */
  'resources.view',
  /** Record who has which skill or gear, and who is on standby. */
  'resources.manage',
  'portal.use',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ALL: Permission[] = PERMISSIONS.filter((p) => p !== 'portal.use');

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  // AUTH-005: no live case data by default. Support access is granted per agency, time-limited (Phase 9).
  super_admin: [],
  agency_admin: ALL,
  // Court scheduling and the quartermaster's job are granted to chosen supervisors (per-user permissions).
  supervisor: ALL.filter((p) => !p.startsWith('admin.') && p !== 'court.schedule' && p !== 'fleet.manage' && p !== 'training.manage' && p !== 'pio.write' && p !== 'pio.approve' && p !== 'personnel.manage' && p !== 'ews.review' && p !== 'hr.manage' && p !== 'policies.manage' && p !== 'dues.manage' && p !== 'ia.manage' && p !== 'resources.manage' && p !== 'warrants.sign' && p !== 'warrants.enter').concat(['admin.rules']),
  clerk: [
    'papers.read',
    'papers.intake',
    'papers.review',
    'papers.close',
    'documents.upload',
    'documents.view',
    'payments.record',
    'custody.scan',
    'custody.court_return',
    'audit.read',
    'bolo.manage',
    'affidavits.notarize',
    'directory.add',
    'reports.read',
    'permits.manage',
    'directory.view',
    'court.view',
    'casefiles.share',
    'warrants.view',
  ],
  process_server: [
    'papers.read_assigned',
    'documents.view',
    'custody.scan',
    'attempts.record',
    'dispositions.finalize_non_service',
    'affidavits.sign',
    'reports.write',
    'reports.read',
    'directory.view',
    'court.view',
    'casefiles.share',
    'warrants.view',
    'warrants.request',
  ],
  // Law office / court portal: only their own papers, enforced by customer_id in every portal query.
  customer: ['portal.use'],
  auditor: ['papers.read', 'documents.view', 'audit.read', 'reports.read'],
  // Live duty board, alerts and name/address lookup. No case files, fees or documents.
  dispatcher: ['dispatch.view', 'safety.respond', 'directory.view', 'court.view', 'warrants.view'],
  // The court: schedules trials, hearings and security; decides what happens to evidence in its cases.
  magistrate: ['court.view', 'court.schedule', 'directory.view', 'warrants.sign', 'portal.use'],
  // The prosecutor's office: court calendar, the directory (who to call, who's working), and case files shared with them.
  solicitor: ['court.view', 'directory.view', 'casefiles.view'],
};

/** Roles that must use 2FA on every login (SEC-001). */
export const ROLES_REQUIRING_2FA: Role[] = ['super_admin', 'agency_admin', 'supervisor', 'dispatcher', 'magistrate', 'solicitor'];

/**
 * Products. Each agency buys one:
 * - civilhound: civil process only (civilprocesssoftware.com).
 * - bluehq: the whole records system, civil process included (803systems.com).
 * Agencies with no product set are BlueHQ (everything), so nothing an agency already uses disappears.
 */
export const PRODUCTS = ['bluehq', 'civilhound'] as const;
export type Product = (typeof PRODUCTS)[number];
export const PRODUCT_LABELS: Record<Product, string> = { bluehq: 'BlueHQ', civilhound: 'CivilHound' };
export function productOf(settings: { product?: unknown } | null | undefined): Product {
  return settings?.product === 'civilhound' ? 'civilhound' : 'bluehq';
}

/** What only BlueHQ agencies get: reports, cases, evidence, warrants, court, the agency's people and fleet, permits. */
export const BLUEHQ_PERMISSIONS: readonly Permission[] = [
  'reports.write', 'reports.read', 'reports.approve', 'evidence.manage', 'court.view', 'court.schedule', 'directory.view', 'admin.org',
  'fleet.manage', 'permits.manage', 'casefiles.share', 'casefiles.view', 'cases.investigate', 'warrants.view', 'warrants.request', 'warrants.enter', 'warrants.sign',
  'training.view', 'training.manage', 'tips.manage', 'pio.write', 'pio.approve', 'personnel.manage', 'ews.review', 'hr.manage', 'schools.manage', 'ia.manage', 'command.start', 'resources.view', 'resources.manage',
];

/** API paths that belong to BlueHQ (some, like messages and My gear, need no permission, so they're closed by path). */
const BLUEHQ_API = [
  /^\/api\/(org|mail|case-files|my-cases|investigations|photos|warrants|warrant-requests|fleet|equipment|permits|reports|arrests|evidence|supplements|tows|tickets|me\/attention|me\/mileage|on-call|phone-numbers|me\/equipment-request|evictions|me\/landing|announcements|ask|training|tips|notices|cad|pio|intel|uof|personnel|ews|hr|evaluations|inspections|narcan|passon|schools|lockdown-drills|forms|escorts|statutes|policies|call-numbers|dues|funding|field-bolos|work-files|applicants|foia|bwc|events|sro|outside-contacts|shutoffs|arrest-feed|house-checks|public\/house-checks|daily-logs|ia|command|resources|map-layers)(\/|$)/,
  /^\/api\/papers\/active-search$/,
  /^\/api\/court(\/|$)/,
  /^\/api\/directory$/,
  /^\/api\/cases\/[^/]+\/(track|investigation|workspace|entries)$/,
  /^\/api\/cases\/(activity|entries\/.*)$/,
  /^\/api\/me\/gear(\/|$)/,
  /^\/api\/fleet\/[^/]+\/inspections$/,
];
export const isBlueHQApi = (path: string) => BLUEHQ_API.some((re) => re.test(path));

/**
 * What serving civil papers takes. A patrol deputy (civil access "lookup") has none of these: they can
 * still look up active civil papers and evictions, but papers aren't assigned to them and they don't serve.
 */
export const CIVIL_SERVE_PERMISSIONS: readonly Permission[] = ['papers.read_assigned', 'custody.scan', 'attempts.record', 'dispositions.finalize_non_service', 'affidavits.sign'];
export type CivilAccess = 'full' | 'lookup';
export const CIVIL_ACCESS_LABELS: Record<CivilAccess, string> = { full: 'Civil deputy — serves civil papers', lookup: 'Patrol deputy — looks up civil papers and evictions only' };

export function permissionsFor(role: Role, extra: string[] = [], product: Product = 'bluehq', civilAccess: CivilAccess | null = null, access: { profile?: string[] | null; denied?: string[] } = {}): Set<Permission> {
  // An access profile (set up by the agency admin) replaces the role's starting set; never for agency admins,
  // so an admin can't lock the agency out.
  const custom = role !== 'agency_admin' && role !== 'super_admin' && role !== 'customer';
  const start = custom && access.profile ? access.profile.filter((p) => (PERMISSIONS as readonly string[]).includes(p) && p !== 'portal.use') : ROLE_PERMISSIONS[role];
  const set = new Set<Permission>(start as Permission[]);
  for (const p of extra) if ((PERMISSIONS as readonly string[]).includes(p)) set.add(p as Permission);
  if (custom) for (const p of access.denied ?? []) set.delete(p as Permission);
  if (product === 'civilhound') for (const p of BLUEHQ_PERMISSIONS) set.delete(p);
  if (civilAccess === 'lookup' && role === 'process_server') for (const p of CIVIL_SERVE_PERMISSIONS) set.delete(p);
  return set;
}

/** Every permission in plain words, grouped, for the administrator's checkboxes. */
export const PERMISSION_INFO: Record<Exclude<Permission, 'portal.use'>, { group: string; label: string }> = {
  'papers.read': { group: 'Civil process', label: 'See all civil papers' },
  'papers.read_assigned': { group: 'Civil process', label: 'See papers assigned to them' },
  'papers.intake': { group: 'Civil process', label: 'Enter new papers' },
  'papers.review': { group: 'Civil process', label: 'Review papers' },
  'papers.hold': { group: 'Civil process', label: 'Put papers on hold' },
  'papers.mark_urgent': { group: 'Civil process', label: 'Mark papers urgent' },
  'papers.switch_rule_version': { group: 'Civil process', label: 'Switch a paper’s service rules' },
  'papers.close': { group: 'Civil process', label: 'Close papers' },
  'documents.upload': { group: 'Civil process', label: 'Upload documents' },
  'documents.view': { group: 'Civil process', label: 'View documents' },
  'payments.record': { group: 'Civil process', label: 'Record payments' },
  'payments.override': { group: 'Civil process', label: 'Override payments' },
  'custody.scan': { group: 'Civil process', label: 'Scan papers in and out' },
  'custody.authorize_transfer': { group: 'Civil process', label: 'Authorize paper transfers' },
  'custody.mark_lost': { group: 'Civil process', label: 'Mark a paper lost' },
  'custody.court_return': { group: 'Civil process', label: 'Return papers to court' },
  'attempts.record': { group: 'Civil process', label: 'Serve papers (record attempts)' },
  'attempts.calendar_override': { group: 'Civil process', label: 'Override the service calendar' },
  'dispositions.finalize_non_service': { group: 'Civil process', label: 'Finalize non-service' },
  'dispositions.override': { group: 'Civil process', label: 'Override dispositions' },
  'dispositions.review': { group: 'Civil process', label: 'Review dispositions' },
  'affidavits.sign': { group: 'Civil process', label: 'Sign affidavits' },
  'affidavits.amend': { group: 'Civil process', label: 'Amend affidavits' },
  'affidavits.notarize': { group: 'Civil process', label: 'Notarize affidavits' },
  'setout.schedule': { group: 'Civil process', label: 'Schedule set-outs (evictions)' },
  'sync.review_conflicts': { group: 'Civil process', label: 'Review offline sync conflicts' },
  'reports.write': { group: 'Patrol & records', label: 'Write incident reports, tickets, tows' },
  'reports.read': { group: 'Patrol & records', label: 'Read everyone’s reports' },
  'reports.approve': { group: 'Patrol & records', label: 'Approve reports (supervisor review)' },
  'evidence.manage': { group: 'Patrol & records', label: 'Evidence custodian' },
  'bolo.manage': { group: 'Patrol & records', label: 'Manage BOLOs' },
  'dispatch.view': { group: 'Patrol & records', label: 'Dispatch board' },
  'safety.respond': { group: 'Patrol & records', label: 'Respond to deputy safety alerts' },
  'command.start': { group: 'Patrol & records', label: 'Start command boards' },
  'resources.view': { group: 'Patrol & records', label: 'See resources on duty' },
  'resources.manage': { group: 'Patrol & records', label: 'Record who has which skill or gear' },
  'permits.manage': { group: 'Patrol & records', label: 'Scrap metal permits' },
  'schools.manage': { group: 'Patrol & records', label: 'SRO: schools, drills, desk reports' },
  'tips.manage': { group: 'Patrol & records', label: 'Work anonymous tips' },
  'cases.investigate': { group: 'Investigations & courts', label: 'Investigator: adopt and work cases' },
  'casefiles.share': { group: 'Investigations & courts', label: 'Share case files with the solicitor' },
  'casefiles.view': { group: 'Investigations & courts', label: 'Record case outcomes (solicitor)' },
  'court.view': { group: 'Investigations & courts', label: 'See court dates' },
  'court.schedule': { group: 'Investigations & courts', label: 'Schedule court & court security' },
  'warrants.view': { group: 'Investigations & courts', label: 'See warrants' },
  'warrants.request': { group: 'Investigations & courts', label: 'Request warrants' },
  'warrants.enter': { group: 'Investigations & courts', label: 'Warrants office: enter warrants' },
  'warrants.sign': { group: 'Investigations & courts', label: 'Sign warrants (judge)' },
  'pio.write': { group: 'Investigations & courts', label: 'PIO: write releases' },
  'pio.approve': { group: 'Investigations & courts', label: 'PIO: approve releases' },
  'directory.view': { group: 'People & agency', label: 'Directory and org chart' },
  'directory.add': { group: 'People & agency', label: 'Add to the directory' },
  'admin.org': { group: 'People & agency', label: 'Edit the org chart & shifts' },
  'schedule.manage': { group: 'People & agency', label: 'Set deputy schedules' },
  'fleet.manage': { group: 'People & agency', label: 'Quartermaster: fleet & equipment' },
  'training.view': { group: 'People & agency', label: 'See everyone’s training' },
  'training.manage': { group: 'People & agency', label: 'Training officer' },
  'personnel.manage': { group: 'People & agency', label: 'Every write-up, commendation & complaint' },
  'ews.review': { group: 'People & agency', label: 'Early warning & use of force review' },
  'hr.manage': { group: 'People & agency', label: 'HR: employees, pay, applicants' },
  'ia.manage': { group: 'People & agency', label: 'Internal affairs' },
  'policies.manage': { group: 'People & agency', label: 'Publish policies & see who signed' },
  'dues.manage': { group: 'People & agency', label: 'Monthly dues (secretary)' },
  'audit.read': { group: 'Administration', label: 'Read the audit log' },
  'admin.users': { group: 'Administration', label: 'Manage users' },
  'admin.config': { group: 'Administration', label: 'Agency settings' },
  'admin.rules': { group: 'Administration', label: 'Service rules' },
};
export type AccessProfile = { id: string; name: string; permissions: string[] };
